import { Color } from "@repo/app-types/constants/colors";
import { getOutcomeImpactCopy } from "@repo/app-types/helpers/outcomeImpactCopy";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconBolt } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";

/**
 * Build the tooltip content for criteria outcome impact.
 * This is shared between the impact pill and the criterion dialog impact card.
 */
export function buildCriteriaOutcomeImpactTooltip(params: {
  rank: number;
  totalCriteria: number;
  netConversions: number;
  averageRevenuePerOpp?: number;
  outcomeNoun: string | undefined;
  outcomePluralNoun?: string | undefined;
  isRevenueOutcome?: boolean | undefined;
  isTeammateLevel: boolean;
}): ReactNode {
  const {
    rank,
    totalCriteria,
    netConversions,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
    isRevenueOutcome,
    isTeammateLevel,
  } = params;
  const impactCopy = getOutcomeImpactCopy({
    netConversions,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
    isRevenueOutcome,
  });

  const impactDescription = isTeammateLevel
    ? `Improving on this criterion could ${impactCopy.sentence}`
    : `Coaching your team on this criterion could ${impactCopy.sentence}`;

  return (
    <div className="max-w-xs text-sm">
      #{rank} of {totalCriteria} criteria in estimated impact on{" "}
      {impactCopy.noun}. {impactDescription}
    </div>
  );
}

interface CriteriaOutcomeCorrelationImpactPillProps {
  /** Rank of this criteria (1 = highest impact) */
  rank: number;
  /** Total number of criteria being ranked */
  totalCriteria: number;
  /** Expected extra conversions per 1000 calls from coaching */
  netConversions: number;
  averageRevenuePerOpp?: number;
  /** Noun form of the outcome (e.g., "enrollment", "win") */
  outcomeNoun: string | undefined;
  /** Per-playbook plural noun override (see `getOutcomePluralNoun`). */
  outcomePluralNoun?: string | undefined;
  /** Whether this is the revenue outcome (see `isRevenueOutcomeDefinition`). */
  isRevenueOutcome?: boolean | undefined;
  /** Whether the pill is in collapsed mode (i.e. when a criteria is selected and there's less room) */
  isCollapsed: boolean;
  /** Whether this pill is shown at the teammate level playbook view vs company level playbook view (affects tooltip text) */
  isTeammateLevel?: boolean;
}

export const CriteriaOutcomeCorrelationImpactPill: FC<
  CriteriaOutcomeCorrelationImpactPillProps
> = ({
  isCollapsed = false,
  rank,
  totalCriteria,
  netConversions,
  averageRevenuePerOpp,
  outcomeNoun,
  outcomePluralNoun,
  isRevenueOutcome,
  isTeammateLevel = false,
}) => {
  const tooltipContent = buildCriteriaOutcomeImpactTooltip({
    rank,
    totalCriteria,
    netConversions,
    averageRevenuePerOpp,
    outcomeNoun,
    outcomePluralNoun,
    isRevenueOutcome,
    isTeammateLevel,
  });

  const label = isCollapsed ? (
    <IconBolt className="size-3" />
  ) : (
    <span className="flex items-center gap-1">
      <IconBolt className="size-3" />
      High impact
    </span>
  );

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">
        <Pill
          color={Color.Emerald}
          size={Size.SMALL}
          label={label}
          className={isCollapsed ? "px-1" : undefined}
        />
      </TooltipHoverTarget>
      <SimpleTooltip>{tooltipContent}</SimpleTooltip>
    </TooltipTrigger>
  );
};
